/**
 * ErrorBoundary global — attrape toute erreur de rendu React non gérée
 * (crash composant, chunk lazy introuvable, etc.) et affiche une page
 * de récupération au lieu d'un écran blanc total.
 *
 * Usage dans App.tsx :
 *   <ErrorBoundary>
 *     <Suspense fallback={<PageFallback />}>
 *       <Routes>…</Routes>
 *     </Suspense>
 *   </ErrorBoundary>
 *
 * React n'expose pas encore de hook useErrorBoundary pour les class
 * components — ce pattern class reste donc nécessaire en React 18.
 */
import { Component, type ReactNode, type ErrorInfo } from 'react';
import { Sticker } from '@/components/passage/Sticker';

// ─── Fallback UI ────────────────────────────────────────────────────────────

function ErrorFallback({
  error,
  onReset,
}: {
  error: Error;
  onReset: () => void;
}) {
  const isChunkError =
    error.message.includes('Failed to fetch dynamically imported module') ||
    error.message.includes('Importing a module script failed') ||
    error.message.includes('Unable to preload CSS');

  return (
    <section className="container flex flex-col items-center py-24 text-center">
      <Sticker color="tomate" rotate={2} shadow="sm">Erreur inattendue</Sticker>

      <div className="mt-4 text-[6rem] font-black leading-none -tracking-[0.05em] text-passage-ink">
        :(
      </div>

      <h1 className="mt-2 text-2xl font-extrabold -tracking-[0.02em] text-passage-ink">
        {isChunkError
          ? 'Impossible de charger la page.'
          : "Une erreur s'est produite."}
      </h1>

      <p className="mt-3 max-w-sm text-sm leading-relaxed text-passage-ink-soft">
        {isChunkError
          ? 'Vérifiez votre connexion internet et rechargez la page. Si le problème persiste, une mise à jour a peut-être été déployée.'
          : "L'application a rencontré une erreur inattendue. Rechargez la page ou revenez à l'accueil."}
      </p>

      <div className="mt-8 flex flex-wrap justify-center gap-3">
        <button
          type="button"
          onClick={onReset}
          className="rounded-xl border-2 border-passage-ink bg-passage-ink px-8 py-3.5 text-sm font-extrabold text-white shadow-[4px_4px_0_#E63946] transition hover:translate-y-[2px] hover:shadow-[2px_2px_0_#E63946]"
        >
          Recharger la page
        </button>
        <a
          href="/"
          className="rounded-xl border-2 border-passage-ink px-8 py-3.5 text-sm font-extrabold text-passage-ink shadow-[4px_4px_0_rgba(31,58,95,0.15)] transition hover:translate-y-[2px] hover:shadow-[2px_2px_0_rgba(31,58,95,0.15)]"
        >
          Retour à l'accueil
        </a>
      </div>

      {/* Détail technique — visible uniquement en dev */}
      {import.meta.env.DEV && (
        <details className="mt-10 max-w-xl text-left">
          <summary className="cursor-pointer text-xs font-semibold text-passage-ink-soft">
            Détail technique (dev uniquement)
          </summary>
          <pre className="mt-2 overflow-auto rounded-xl border-2 border-passage-ink/20 bg-slate-50 p-4 text-xs text-destructive">
            {error.stack ?? error.message}
          </pre>
        </details>
      )}
    </section>
  );
}

// ─── ErrorBoundary class ────────────────────────────────────────────────────

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // En prod : logger l'erreur côté client pour le monitoring futur.
    // On évite console.error en faveur d'un log structuré si disponible.
    if (import.meta.env.PROD) {
      // Point d'extension : brancher Sentry / OpenTelemetry ici.
      // eslint-disable-next-line no-console
      console.error('[ErrorBoundary]', error, info.componentStack);
    }
  }

  handleReset = (): void => {
    // Tenter un reset de l'état avant de recharger — suffit pour les
    // erreurs transitoires, mais pour les chunk errors un reload complet
    // est plus sûr (le nouveau bundle sera téléchargé).
    this.setState({ error: null });
    window.location.reload();
  };

  override render(): ReactNode {
    if (this.state.error) {
      return (
        <ErrorFallback
          error={this.state.error}
          onReset={this.handleReset}
        />
      );
    }
    return this.props.children;
  }
}
